import { ApiError } from "@/lib/apiClient";
import {
  AbandonedCartBulkMessage,
  CreateAbandonedCartBulkMessageDto,
  UpdateAbandonedCartBulkMessageDto,
  FetchAbandonedCartOptions,
  AbandonedCartFilters,
  AbandonedCartCustomer,
} from "@/types/models/marketing";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { resetStoreSpecificState } from "@/store/actions/storeActions";

export interface AbandonedCartState {
  bulkMessages: AbandonedCartBulkMessage[];
  bulkMessagesLoading: boolean;
  bulkMessagesError: ApiError | null;
  bulkMessagesPagination: {
    page: number;
    total: number;
    pageSize: number;
    totalPages: number;
  };

  customers: AbandonedCartCustomer[];
  customersLoaded: boolean;
  customersLoading: boolean;
  customersError: ApiError | null;
  customersPagination: {
    page: number;
    total: number;
    pageSize: number;
    totalPages: number;
  };

  currentBulkMessage: AbandonedCartBulkMessage | null;
  bulkMessageLoading: boolean;
  bulkMessageError: ApiError | null;

  formState: {
    loading: boolean;
    error: ApiError | null;
    success: boolean;
  };

  filters: AbandonedCartFilters;
  fetchOptions: FetchAbandonedCartOptions;
}

const initialState: AbandonedCartState = {
  bulkMessages: [],
  bulkMessagesLoading: false,
  bulkMessagesError: null,
  bulkMessagesPagination: {
    page: 1,
    total: 0,
    pageSize: 10,
    totalPages: 0,
  },

  customers: [],
  customersLoaded: false,
  customersLoading: false,
  customersError: null,
  customersPagination: {
    page: 1,
    total: 0,
    pageSize: 10,
    totalPages: 0,
  },

  currentBulkMessage: null,
  bulkMessageLoading: false,
  bulkMessageError: null,

  formState: {
    loading: false,
    error: null,
    success: false,
  },

  filters: {},
  fetchOptions: {
    page: 1,
    page_size: 10,
    sort_by: "created_at",
    sort_order: "desc",
  },
};

const abandonedCartSlice = createSlice({
  name: "abandonedCart",
  initialState,
  reducers: {
    fetchAbandonedCartBulkMessagesRequest: (
      state,
      action: PayloadAction<FetchAbandonedCartOptions>,
    ) => {
      state.bulkMessagesLoading = true;
      state.bulkMessagesError = null;
      state.fetchOptions = { ...state.fetchOptions, ...action.payload };
    },
    fetchAbandonedCartBulkMessagesSuccess: (
      state,
      action: PayloadAction<{
        data: AbandonedCartBulkMessage[];
        pagination: any;
      }>,
    ) => {
      state.bulkMessagesLoading = false;
      state.bulkMessages = action.payload.data;
      state.bulkMessagesPagination = action.payload.pagination;
    },
    fetchAbandonedCartBulkMessagesFailure: (
      state,
      action: PayloadAction<ApiError>,
    ) => {
      state.bulkMessagesLoading = false;
      state.bulkMessagesError = action.payload;
    },

    fetchAbandonedCartCustomersRequest: (
      state,
      _action: PayloadAction<FetchAbandonedCartOptions>,
    ) => {
      state.customersLoading = true;
      state.customersError = null;
    },
    fetchAbandonedCartCustomersSuccess: (
      state,
      action: PayloadAction<{ data: AbandonedCartCustomer[]; pagination: any }>,
    ) => {
      state.customersLoading = false;
      state.customersLoaded = true;
      state.customers = action.payload.data;
      state.customersPagination = action.payload.pagination;
    },
    fetchAbandonedCartCustomersFailure: (
      state,
      action: PayloadAction<ApiError>,
    ) => {
      state.customersLoading = false;
      state.customersLoaded = true;
      state.customersError = action.payload;
    },

    createAbandonedCartBulkMessageRequest: (
      state,
      _action: PayloadAction<CreateAbandonedCartBulkMessageDto>,
    ) => {
      state.formState.loading = true;
      state.formState.error = null;
      state.formState.success = false;
    },
    createAbandonedCartBulkMessageSuccess: (
      state,
      action: PayloadAction<AbandonedCartBulkMessage>,
    ) => {
      state.formState.loading = false;
      state.formState.success = true;
      state.bulkMessages = [action.payload, ...state.bulkMessages];
    },
    createAbandonedCartBulkMessageFailure: (
      state,
      action: PayloadAction<ApiError>,
    ) => {
      state.formState.loading = false;
      state.formState.error = action.payload;
    },

    updateAbandonedCartBulkMessageRequest: (
      state,
      _action: PayloadAction<{
        id: string;
        data: UpdateAbandonedCartBulkMessageDto;
      }>,
    ) => {
      state.formState.loading = true;
      state.formState.error = null;
      state.formState.success = false;
    },
    updateAbandonedCartBulkMessageSuccess: (
      state,
      action: PayloadAction<AbandonedCartBulkMessage>,
    ) => {
      state.formState.loading = false;
      state.formState.success = true;
      state.currentBulkMessage = action.payload;

      const index = state.bulkMessages.findIndex(
        (item) => item.id === action.payload.id,
      );
      if (index !== -1) {
        state.bulkMessages[index] = action.payload;
      }
    },
    updateAbandonedCartBulkMessageFailure: (
      state,
      action: PayloadAction<ApiError>,
    ) => {
      state.formState.loading = false;
      state.formState.error = action.payload;
    },

    deleteAbandonedCartBulkMessageRequest: (
      state,
      _action: PayloadAction<{ id: string }>,
    ) => {
      state.formState.loading = true;
      state.formState.error = null;
    },
    deleteAbandonedCartBulkMessageSuccess: (
      state,
      action: PayloadAction<{ id: string }>,
    ) => {
      state.formState.loading = false;
      state.bulkMessages = state.bulkMessages.filter(
        (item) => item.id !== action.payload.id,
      );
    },
    deleteAbandonedCartBulkMessageFailure: (
      state,
      action: PayloadAction<ApiError>,
    ) => {
      state.formState.loading = false;
      state.formState.error = action.payload;
    },

    sendAbandonedCartBulkMessageRequest: (
      state,
      _action: PayloadAction<{ id: string }>,
    ) => {
      state.formState.loading = true;
      state.formState.error = null;
    },
    sendAbandonedCartBulkMessageSuccess: (
      state,
      action: PayloadAction<AbandonedCartBulkMessage>,
    ) => {
      state.formState.loading = false;
      const index = state.bulkMessages.findIndex(
        (item) => item.id === action.payload.id,
      );
      if (index !== -1) {
        state.bulkMessages[index] = action.payload;
      }
    },
    sendAbandonedCartBulkMessageFailure: (
      state,
      action: PayloadAction<ApiError>,
    ) => {
      state.formState.loading = false;
      state.formState.error = action.payload;
    },

    setFilters: (state, action: PayloadAction<AbandonedCartFilters>) => {
      state.filters = action.payload;
    },
    resetFormState: (state) => {
      state.formState = {
        loading: false,
        error: null,
        success: false,
      };
    },
    clearCurrentBulkMessage: (state) => {
      state.currentBulkMessage = null;
      state.bulkMessageError = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetStoreSpecificState, () => initialState);
  },
});

export const {
  fetchAbandonedCartBulkMessagesRequest,
  fetchAbandonedCartBulkMessagesSuccess,
  fetchAbandonedCartBulkMessagesFailure,
  fetchAbandonedCartCustomersRequest,
  fetchAbandonedCartCustomersSuccess,
  fetchAbandonedCartCustomersFailure,
  createAbandonedCartBulkMessageRequest,
  createAbandonedCartBulkMessageSuccess,
  createAbandonedCartBulkMessageFailure,
  updateAbandonedCartBulkMessageRequest,
  updateAbandonedCartBulkMessageSuccess,
  updateAbandonedCartBulkMessageFailure,
  deleteAbandonedCartBulkMessageRequest,
  deleteAbandonedCartBulkMessageSuccess,
  deleteAbandonedCartBulkMessageFailure,
  sendAbandonedCartBulkMessageRequest,
  sendAbandonedCartBulkMessageSuccess,
  sendAbandonedCartBulkMessageFailure,
  setFilters,
  resetFormState,
  clearCurrentBulkMessage,
} = abandonedCartSlice.actions;

export default abandonedCartSlice.reducer;
